import {type FormEvent, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {env} from '@/matic/config/env';
import {getUserManager} from '@/matic/lib/auth/user-manager';
import {LocalUserManager} from '@/matic/lib/auth/local-user-manager';
import {Button} from '@/matic/components/ui/button';

/**
 * Email + password sign-in for `VITE_AUTH_MODE=local-jwt` (the no-AWS deployment). The
 * token comes back from the identity service and is stored through the OIDC
 * `UserManager` surface so the rest of the app never knows the difference.
 */
export function LocalLoginForm() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const response = await fetch(
        `${env.API_URL}/api/v1/identity/auth/local/login`,
        {
          method: 'POST',
          // eslint-disable-next-line @typescript-eslint/naming-convention -- HTTP header
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({email, password}),
        }
      );
      if (!response.ok) {
        setError(
          response.status === 401
            ? 'Invalid email or password.'
            : `Sign-in failed (${response.status}).`
        );
        return;
      }

      // eslint-disable-next-line @typescript-eslint/naming-convention
      const body = (await response.json()) as {id_token: string};
      const manager = getUserManager();
      if (!manager) {
        setError('Authentication is not configured in this build.');
        return;
      }

      await manager.storeUser(LocalUserManager.userFromIdToken(body.id_token));
      navigate('/home', {replace: true});
    } catch {
      setError("Couldn't reach the sign-in service.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-lg border border-neutral-200 bg-white p-6 shadow-sm"
      >
        <h1 className="text-lg font-semibold text-neutral-900">
          {env.APP_NAME}
        </h1>
        <p className="mt-1 text-sm text-neutral-500">Sign in to continue.</p>
        <label className="mt-5 block text-sm font-medium text-neutral-700">
          Email
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
          />
        </label>
        <label className="mt-3 block text-sm font-medium text-neutral-700">
          Password
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
          />
        </label>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        <Button
          type="submit"
          variant="dark"
          className="mt-5 w-full"
          disabled={busy}
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </div>
  );
}
